import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from './types';
import { MediaFile } from './media';
import { INCIDENTS_KEY, INCIDENTS_QUERY_KEYS } from './incidents';
import { MAP_KEY, MAP_QUERY_KEYS } from './map';

export const PERSONS_KEY = 'persons';
export const PERSONS_QUERY_KEYS = {
  LIST: 'list',
  INFINITE: 'infinite'
} as const;

export const VICTIM_REUNIFICATION_KEY = 'victim-reunification';

export interface PersonBase {
  incidentId?: string;
  type?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: number;
  age?: number;
  height?: number;
  weight?: number;
  hair?: number;
  eyes?: number;
  features?: number[];
  additionalInfo?: string;
  status?: string;
  isMatched?: boolean;
  isConnected?: boolean;
  connectedDate?: string;
  isReunified?: boolean;
  createdDate?: string;
  locationId?: string;
  unitId?: string;
  department?: string;
  foundLocation?: string;
  restrictions?: boolean;
}

export interface Person extends PersonBase {
  id?: string;
  fullName?: string;
  matchedPersonId?: string;
  pictureUrl?: string;
  incidentName?: string;
  incidentAddress?: string;
  locationAddress?: string;
  locationName?: string;
  publicContact?: PublicContact;
  matchedPerson?: Person;
  mediaFiles?: MediaFile[];
  /** Number of public requests currently matched to this victim (server-computed). */
  matchedCount?: number;
  /** Face group ID from AWS Rekognition — public persons matching the same victim share the same faceGroupId. */
  faceGroupId?: string;
  /** Sorting priority: 1 = Face match, 2 = Full name match, 3 = Last name match, 4 = Unmatched. */
  sortPriority?: number;
  /** Highest face match similarity score (0-100). Only present for face-matched persons. */
  faceMatchScore?: number;
}

export interface VictimReunificationGroup {
  victim: Person;
  matchedPublicPersons: Person[];
}

export interface PublicContact {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  relationshipToPerson?: number;
}

interface CreatePersonParams {
  person: PersonBase;
  publicContact?: PublicContact;
  recaptchaToken?: string;
}

interface UpdatePersonParams {
  person: PersonBase & { id: string };
}

interface GetPersonsParams {
  incidentId?: string;
  type: 'public' | 'victim';
  skip?: number;
  take: number;
  name?: string;
  gender?: number;
  age?: number;
  height?: number;
  weight?: number;
  hair?: number;
  eyes?: number;
  matchByLastName?: boolean;
  matchByFace?: boolean;
  includeMatchedVictimsForFaceMatch?: boolean;
  isMatched?: boolean;
  isConnected?: boolean;
  connectedDate?: string;
  includeMatchedPerson?: boolean;
}

interface GetVictimReunificationParams {
  incidentId?: string;
  name?: string;
  displayNotContactedOnly?: boolean;
  skip?: number;
  take: number;
}

interface GetPersonByIdParams {
  id: string;
}

interface MatchPersonsParams {
  publicIds: string[];
  victimId: string;
}

interface DeletePersonParams {
  id: string;
}

interface UpdateConnectPersonParams {
  ids: string[];
  isConnected: boolean;
}


// Fetch persons list
export const usePersons = (params: GetPersonsParams) => {
  return useQuery({
    queryKey: [PERSONS_KEY, PERSONS_QUERY_KEYS.LIST, params],
    queryFn: async () => {
      const response = await apis.persons.getPersonsList({ params });
      return response.data as DataCollectionApiResponse<Person>;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });
};

// Fetch persons with infinite loading
export const useInfinitePersons = (params: Omit<GetPersonsParams, 'skip'>) => {
  return useInfiniteQuery<
    DataCollectionApiResponse<Person>,
    Error,
    InfiniteData<DataCollectionApiResponse<Person>>,
    [string, string, GetPersonsParams],
    number
  >({
    queryKey: [PERSONS_KEY, PERSONS_QUERY_KEYS.INFINITE, params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apis.persons.getPersonsList({
        params: {
          ...params,
          skip: pageParam * params.take
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < params.take) return undefined;
      return allPages.length;
    }
  });
};

// Fetch victim reunification groups with infinite loading
export const useInfiniteVictimReunification = (params: Omit<GetVictimReunificationParams, 'skip'>) => {
  return useInfiniteQuery<
    DataCollectionApiResponse<VictimReunificationGroup>,
    Error,
    InfiniteData<DataCollectionApiResponse<VictimReunificationGroup>>,
    [string, Omit<GetVictimReunificationParams, 'skip'>],
    number
  >({
    queryKey: [VICTIM_REUNIFICATION_KEY, params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apis.persons.getVictimReunification({
        params: {
          ...params,
          skip: pageParam * params.take
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < params.take) return undefined;
      return allPages.length;
    }
  });
};

// Fetch person by id
export const usePersonById = (params: GetPersonByIdParams) => {
  return useQuery({
    queryKey: [PERSONS_KEY, params],
    queryFn: async () => {
      const response = await apis.persons.getPersonById({ data: params });
      return response.data as DataApiResponse<Person>;
    },
  });
};

// Create person
export const useCreatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreatePersonParams) => {
      const response = await apis.persons.createPerson({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
    },
  });
};

// Update person
export const useUpdatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdatePersonParams) => {
      const response = await apis.persons.updatePerson({ data: params });
      return response.data as DataApiResponse<Person>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
    },
  });
};

// Delete person
export const useDeletePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeletePersonParams) => {
      const response = await apis.persons.deletePerson({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
    }
  });
};

// Update connect person
export const useUpdateConnectPerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateConnectPersonParams) => {
      const response = await apis.persons.updateConnectPerson({ data: params });
      return response.data as DataApiResponse<Person>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.BY_ID] });
      queryClient.invalidateQueries({ queryKey: [MAP_KEY, MAP_QUERY_KEYS.STATISTICS] });
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VICTIM_REUNIFICATION_KEY] });
    },
  });
};


// Match persons
export const useMatchPersons = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: MatchPersonsParams) => {
      const response = await apis.persons.matchPersons({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.BY_ID] });
      queryClient.invalidateQueries({ queryKey: [MAP_KEY, MAP_QUERY_KEYS.STATISTICS] });
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VICTIM_REUNIFICATION_KEY] });
    },
  });
};

// Unmatch persons
export const useUnmatchVictims = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { victimId: string; publicIds: string[] }) => {
      const response = await apis.persons.unmatchVictims({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INCIDENTS_KEY, INCIDENTS_QUERY_KEYS.BY_ID] });
      queryClient.invalidateQueries({ queryKey: [MAP_KEY, MAP_QUERY_KEYS.STATISTICS] });
      queryClient.invalidateQueries({ queryKey: [PERSONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VICTIM_REUNIFICATION_KEY] });
    },
  });
};
